// CalendarSection — Jul 2026 (Randy's AI Calendar, Staging section #2.5).
// Custom-built renderer (no third-party calendar lib): Month / Week / Day /
// Agenda views, events · tasks · routines · pings, recurrence, reminders via
// the existing web-push engine, organizer-ping Suggestions Gallery, Google
// Calendar one-way import, and the per-silo "silo knows my calendar" toggle.
//
// pt89U (Feb 20, 2026, Randy) — Refactor pass. EventEditorModal (~470
// lines) moved to its own file `components/EventEditorModal.jsx`. This
// file re-exports it for zero-churn on existing importers (StagingTab,
// FamilyCapturePad, OrganizerPingsSection, SmartCaptureModal). Every
// other component in this file stays in-place until the Stage-C sweep
// touches them individually.
import { useEffect, useMemo, useState } from "react";
import {
  CalendarBlank, CaretLeft, CaretRight, CaretDown, CaretUp, Plus, X, Trash, Check,
  ArrowsClockwise, GoogleLogo, Sparkle, ListChecks, Spinner, Bell, BellRinging,
  Tray, UsersThree, Moon, Gift, Trophy, Lock, PencilSimple, Television, Eye, LockKey,
  Palette, Funnel, FunnelSimple, EyeSlash, Prohibit,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { api, getUser } from "@/lib/api";
import { CalendarThemeProvider, useThemeLabel } from "@/lib/CalendarThemeProvider";
import * as T from "@/lib/themeManifest";
import CalendarThemeModal from "@/components/CalendarThemeModal";
import ThemeWordEditorModal from "@/components/ThemeWordEditorModal";
import { useEntitlements } from "@/lib/entitlements";
import { useCreatorProGate } from "@/components/CreatorProGateProvider";
import LockGate from "@/components/LockGate";
import GoogleCalendarSetupGuide from "@/components/GoogleCalendarSetupGuide";
import KidProfilesPanel from "@/components/KidProfilesPanel";
import { useAnchoredScrollTarget } from "@/hooks/useAnchoredCollapse";
import FamilyRewards from "@/components/FamilyRewards";
import FamilyDigest from "@/components/FamilyDigest";
import OrganizerPingsSection from "@/components/OrganizerPingsSection";
import ChoresSection from "@/components/ChoresSection";
import KidChoresModal from "@/components/KidChoresModal";
import KidScheduleModal from "@/components/KidScheduleModal";
import SmartCreateAllChores from "@/components/SmartCreateAllChores";
import InfoTip from "@/components/InfoTip";
import PendingConfirmationsModal from "@/components/PendingConfirmationsModal";
// pt89U (Feb 20, 2026, Randy) — HouseholdPanel extracted to its own file.
// Was ~495 lines inline; moving it out drops CalendarSection.jsx to
// ~1060 lines. Zero behavior change; identical function body, same
// props, same JSX.
import HouseholdPanel from "@/components/HouseholdPanel";
import ThemeArchivePanel from "@/components/ThemeArchivePanel";
// pt89U (Feb 20, 2026, Randy) — EventEditorModal extracted to its own
// file. Re-export below preserves the previously public named export
// so external consumers (StagingTab, FamilyCapturePad, OrganizerPingsSection)
// don't need to change their import lines.
import EventEditorModalExt from "@/components/EventEditorModal";
export const EventEditorModal = EventEditorModalExt;

// Feb 2026 — the AI Calendar is now a single collapsible frame that houses
// the calendar grid + four sub-buttons (Calendar Pings, Household Setup,
// Reward store, Family leaderboard). Each toggle defaults to minimized and
// remembers the user's choice per silo — Feb 2026 (Randy pt89b) — via a
// BACKEND-persisted user_prefs bag so state now survives log-out, tab
// close, browser reset AND follows the user to other devices. localStorage
// stays as a hot cache for instant first-render before the backend GET
// completes.
// pt89U (Feb 20, 2026, Randy) — pure constants + tiny helpers hoisted
// to `lib/calendarConstants.js` so the extracted EventEditorModal and
// HouseholdPanel can import the same source. The stateful sticky-scroll
// helpers stay right here because they touch localStorage.
import {
  DOW_MIN as _DOW_MIN, MONTHS, KIND_META, REMINDER_OPTS,
  isoLocal as _isoLocal, parseLocal, addDays, fmt12,
} from "@/lib/calendarConstants";
import { useTrialKeepPrompt } from "@/lib/trial";

// Re-export the two public constants so external consumers (StagingTab,
// FamilyCapturePad, OrganizerPingsSection, SmartCaptureModal) keep working
// without touching their import lines.
export const DOW_MIN = _DOW_MIN;
export const isoLocal = _isoLocal;

const stickyKey = (name, siloId) => `mysilo_cal_${name}_${siloId}`;
const readSticky = (key) => {
  try { return localStorage.getItem(key) === "1"; } catch { return false; }
};
const writeSticky = (key, val) => {
  try { localStorage.setItem(key, val ? "1" : "0"); } catch { /* ignore */ }
};

export default function CalendarSection({ siloId, siloName, siloSlug, inheritedSkus = [], kidView = false }) {
  useTrialKeepPrompt("ai_calendar");
  const { locked, lockStyles } = useEntitlements();
  const { openGate } = useCreatorProGate();
  // Household inheritance (Randy 1A): a personal-silo collaborator rides the
  // owner's ai_calendar unlock — the backend gate enforces the same rule.
  const calLocked = locked("ai_calendar") && !inheritedSkus.includes("ai_calendar");

  if (calLocked) {
    // Feb 2026 — locked state renders as the same collapsed horizontal
    // header the unlocked frame uses, plus a lock chip + super-admin
    // glimmer. Tapping opens the SKU gate; the frame itself never expands.
    return (
      <section className="bg-white border hairline rounded-lg" data-testid="calendar-section-locked">
        <LockGate locked lockStyles={lockStyles} onLockedClick={() => openGate("ai_calendar")} block>
          <div className="flex items-center gap-2 px-5 py-3 cursor-pointer">
            <CalendarBlank size={16} weight="duotone" className="text-[#4A5D4E]" />
            <h3 className="font-serif-display text-lg text-[#1C1B1A]">AI Calendar</h3>
            <span className="text-[9px] uppercase tracking-[0.18em] text-[#8A6A24] bg-[#F8F1E2] border border-[#EAD9AE] rounded-full px-2 py-0.5 flex items-center gap-1">
              <Lock size={10} weight="duotone" /> Unlock to turn on
            </span>
            <span className="flex-1" />
            <span className="text-[10px] text-[#6B6A68] italic">events · tasks · routines · pings · rewards</span>
          </div>
        </LockGate>
      </section>
    );
  }
  // pt89T (Feb 20, 2026, Randy) — Stage-A plumbing. Every child that
  // needs a theme-aware label reads it via `useThemeLabel(...)`, which
  // resolves against the silo's persisted `theme_map` (Mongo → context)
  // and falls back to the hardcoded default from themeManifest.js.
  // Wrapping at the outer boundary here means the whole subtree —
  // CalendarInner, EventEditorModal, DayList, etc. — has access.
  return (
    <CalendarThemeProvider siloId={siloId}>
      <CalendarInner siloId={siloId} siloName={siloName} inheritedSkus={inheritedSkus} kidView={kidView} />
    </CalendarThemeProvider>
  );
}

function CalendarInner({ siloId, siloName, inheritedSkus = [], kidView = false }) {
  // pt89T (Feb 20, 2026, Randy) — Stage-A theme label lookups. Fall back
  // to the hardcoded defaults from `themeManifest.js` when the silo has
  // no override. Reading them up here means every render below refers
  // to the same resolved value; no per-render re-resolves.
  const tCalendarTitle             = useThemeLabel(T.THEME_KEY_CALENDAR_TITLE);
  const tCalendarSubtitle          = useThemeLabel(T.THEME_KEY_CALENDAR_SUBTITLE);
  const tTaskSingular              = useThemeLabel(T.THEME_KEY_CALENDAR_TASK_SINGULAR);
  const tTaskPlural                = useThemeLabel(T.THEME_KEY_CALENDAR_TASK_PLURAL);
  const tPendingConfirmations      = useThemeLabel(T.THEME_KEY_CALENDAR_PENDING);
  const tToday                     = useThemeLabel(T.THEME_KEY_CALENDAR_TODAY);
  const tSmartCreateTitle          = useThemeLabel(T.THEME_KEY_CALENDAR_SMART_CREATE_TITLE);
  // pt89U Stage-C additions — sub-button labels. Hoisted so conditional
  // rendering below never triggers a React-hooks-order violation.
  const tPingPlural                = useThemeLabel(T.THEME_KEY_CALENDAR_PING_PLURAL, "Calendar Pings");
  const tHouseholdTitle            = useThemeLabel(T.THEME_KEY_HOUSEHOLD_TITLE, "Household Setup");
  const tThemeArchiveLabel         = useThemeLabel(T.THEME_KEY_THEME_ARCHIVE_TITLE, "Theme Archive");
  const tRewardStoreLabel          = useThemeLabel(T.THEME_KEY_HOUSEHOLD_REWARD_STORE, "Reward store");
  const tLeaderboardLabel          = useThemeLabel(T.THEME_KEY_HOUSEHOLD_LEADERBOARD, "Family leaderboard");
  const tDigestLabel               = useThemeLabel(T.THEME_KEY_HOUSEHOLD_DIGEST, "Family digest");

  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(() => new Date());
  const [data, setData] = useState({ events: [], occurrences: [], streaks: {}, unscheduled_tasks: [] });
  const [settings, setSettings] = useState(null);
  const [editor, setEditor] = useState(null); // {event?, defaults?}
  const [peeker, setPeeker] = useState(null); // kidView read-only drawer
  const [showTasks, setShowTasks] = useState(false);
  const [syncBusy, setSyncBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [showInbox, setShowInbox] = useState(false);
  // Feb 2026 pt89h (Randy) — pending chore-confirmations count/modal.
  const [confirmCount, setConfirmCount] = useState(0);
  const [showConfirmations, setShowConfirmations] = useState(false);
  // Feb 2026 — the outer AI Calendar frame + the four sub-buttons all default
  // to minimized and remember the user's choice per silo. See stickyKey up top.
  const kFrame = stickyKey("frame_open", siloId);
  const kPings = stickyKey("btn_pings", siloId);
  const kHousehold = stickyKey("btn_household", siloId);
  const kThemeArchive = stickyKey("btn_theme_archive", siloId);
  const kRewards = stickyKey("btn_rewards", siloId);
  const kLeader = stickyKey("btn_leaderboard", siloId);
  const kDigest = stickyKey("btn_digest", siloId);
  const [frameOpen, setFrameOpenState] = useState(() => readSticky(kFrame));
  // Iter160t (platform chevron sweep) — anchor-scroll so toggling the
  // AI Calendar frame open/closed doesn't yank the page.
  const { cardRef: sectionRef, withAnchor: withCalendarAnchor } = useAnchoredScrollTarget();
  const [pingsOpen, setPingsOpenState] = useState(() => readSticky(kPings));
  const [householdOpen, setHouseholdOpenState] = useState(() => readSticky(kHousehold));
  const [themeArchiveOpen, setThemeArchiveOpenState] = useState(() => readSticky(kThemeArchive));
  const [rewardsOpen, setRewardsOpenState] = useState(() => readSticky(kRewards));
  const [leaderboardOpen, setLeaderboardOpenState] = useState(() => readSticky(kLeader));
  const [digestOpen, setDigestOpenState] = useState(() => readSticky(kDigest));
  // Feb 2026 (Randy pt89b) — hydrate from backend user-prefs on mount so
  // state follows the user to any device / after any log-out. Backend
  // wins on load; every subsequent toggle writes through to backend AND
  // localStorage. Uses a tiny inline sync helper (fire-and-forget PATCH).
  useEffect(() => {
    let cancelled = false;
    api.get(`/calendar/${siloId}/user-prefs`).then((r) => {
      if (cancelled) return;
      const p = r.data?.prefs || {};
      const set = (v, setter, key) => {
        if (typeof v !== "boolean") return;
        setter(v);
        try { localStorage.setItem(key, v ? "1" : "0"); } catch { /* ignore */ }
      };
      set(p.cal_frame_open, setFrameOpenState, kFrame);
      set(p.sub_pings_open, setPingsOpenState, kPings);
      set(p.sub_household_open, setHouseholdOpenState, kHousehold);
      set(p.sub_rewards_open, setRewardsOpenState, kRewards);
      set(p.sub_leaderboard_open, setLeaderboardOpenState, kLeader);
      set(p.sub_theme_archive_open, setThemeArchiveOpenState, kThemeArchive);
      set(p.sub_digest_open, setDigestOpenState, kDigest);
    }).catch(() => { /* stay on localStorage fallback */ });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siloId]);
  const persistPref = (patch) => {
    // Fire-and-forget; a failed write only means the pref won't survive to
    // another device this session. localStorage still keeps the local user
    // happy on this browser.
    api.patch(`/calendar/${siloId}/user-prefs`, patch).catch(() => {});
  };
  const setFrameOpen = (v) => { withCalendarAnchor(() => setFrameOpenState(v)); writeSticky(kFrame, v); persistPref({ cal_frame_open: !!v }); };
  const setPingsOpen = (v) => { setPingsOpenState(v); writeSticky(kPings, v); persistPref({ sub_pings_open: !!v }); };
  const setHouseholdOpen = (v) => { setHouseholdOpenState(v); writeSticky(kHousehold, v); persistPref({ sub_household_open: !!v }); };
  const setThemeArchiveOpen = (v) => { setThemeArchiveOpenState(v); writeSticky(kThemeArchive, v); persistPref({ sub_theme_archive_open: !!v }); };
  const setRewardsOpen = (v) => { setRewardsOpenState(v); writeSticky(kRewards, v); persistPref({ sub_rewards_open: !!v }); };
  const setLeaderboardOpen = (v) => { setLeaderboardOpenState(v); writeSticky(kLeader, v); persistPref({ sub_leaderboard_open: !!v }); };
  const setDigestOpen = (v) => { setDigestOpenState(v); writeSticky(kDigest, v); persistPref({ sub_digest_open: !!v }); };

  // Feb 20, 2026 (pt89T — Randy) — Calendar header dropdowns state.
  //   • assigneeFilter: "all" or a specific member user_id / kid target_id.
  //     Filters what shows on the calendar grid client-side (backend still
  //     ships everything so the switch is instant and reversible).
  //   • The Google display mode lives on the silo doc (google_display_mode)
  //     so it persists across devices; local state mirrors the loaded value.
  const [assigneeFilter, setAssigneeFilter] = useState("all");
  const [googleModeMenuOpen, setGoogleModeMenuOpen] = useState(false);
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const [rethemeStubOpen, setRethemeStubOpen] = useState(false);
  // Feb 26, 2026 (Randy pt-retheme-pencil-shortcut) — quick access to
  // the word-only theme editor (existing `ThemeWordEditorModal`) from
  // right next to the Re-theme button, so tweaks on an already-applied
  // theme don't require going through the archive panel.
  const [themeWordEditorOpen, setThemeWordEditorOpen] = useState(false);
  // Feb 22, 2026 (Randy Sprint Request 1) — Kind Filter. Filters the
  // calendar grid to a single kind (or hides everything). Persisted per silo
  // in localStorage so it survives page reloads without needing a backend
  // round-trip. Options: active | event | task | routine | ping | hide_all.
  const KIND_FILTER_LS = `mysilo:calendar-kind-filter:${siloId}`;
  const [kindFilter, setKindFilterState] = useState(() => {
    try { return window.localStorage.getItem(KIND_FILTER_LS) || "active"; }
    catch { return "active"; }
  });
  const [kindMenuOpen, setKindMenuOpen] = useState(false);
  const setKindFilter = (v) => {
    setKindFilterState(v);
    try { window.localStorage.setItem(KIND_FILTER_LS, v); } catch { /* */ }
    setKindMenuOpen(false);
  };
  // Re-read from LS when siloId changes (a silo switch remounts, but be safe).
  useEffect(() => {
    try { setKindFilterState(window.localStorage.getItem(KIND_FILTER_LS) || "active"); }
    catch { /* */ }
    // eslint-disable-next-line
  }, [siloId]);
  const googleDisplayMode = settings?.google_display_mode || "active";
  const setGoogleDisplayMode = async (mode) => {
    setGoogleModeMenuOpen(false);
    if (mode === googleDisplayMode) return;
    try {
      await api.patch(`/calendar/${siloId}/google-display-mode`, { mode });
      setSettings((s) => ({ ...(s || {}), google_display_mode: mode }));
      toast.success(mode === "hide" ? "Google events hidden (sync still running in background)."
        : mode === "block" ? "Google sync paused. Tokens preserved."
        : "Google events visible again.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't change Google mode."); }
  };

  // Visible window per view.
  const win = useMemo(() => {
    const c = new Date(cursor);
    if (view === "month") {
      const first = new Date(c.getFullYear(), c.getMonth(), 1);
      const start = addDays(first, -first.getDay());
      return { start, end: addDays(start, 41) };
    }
    if (view === "week") { const start = addDays(c, -c.getDay()); return { start, end: addDays(start, 6) }; }
    if (view === "day") return { start: c, end: c };
    return { start: new Date(), end: addDays(new Date(), 30) }; // agenda
  }, [cursor, view]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/calendar/${siloId}/events`, { params: { start: isoLocal(win.start), end: isoLocal(win.end) } });
      setData(r.data);
    } catch (e) {
      if (e.response?.status !== 402) toast.error(e.response?.data?.detail || "Couldn't load the calendar.", { id: "cal-load" });
    } finally { setLoading(false); }
  };
  const loadSettings = async () => {
    try { const r = await api.get(`/calendar/${siloId}/settings`); setSettings(r.data); } catch { /* */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [siloId, win.start.getTime(), win.end.getTime()]);
  useEffect(() => { loadSettings(); /* eslint-disable-next-line */ }, [siloId]);
  // Feb 2026 pt89h (Randy) — poll pending-chore-confirmations for the
  // Confirmations button on this calendar. Parent-actors only; others get
  // 403 and we silently keep count at 0.
  useEffect(() => {
    if (!settings?.is_owner && !settings?.is_parent_role) { setConfirmCount(0); return; }
    let mounted = true;
    const fetchCount = async () => {
      try {
        const r = await api.get(`/calendar/${siloId}/pending-confirmations`);
        if (mounted) setConfirmCount(r.data?.count || 0);
      } catch { if (mounted) setConfirmCount(0); }
    };
    fetchCount();
    const t = setInterval(fetchCount, 30_000);
    return () => { mounted = false; clearInterval(t); };
  }, [siloId, settings?.is_owner, settings?.is_parent_role]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("mysilo:calendar-refresh", h);
    return () => window.removeEventListener("mysilo:calendar-refresh", h);
    // eslint-disable-next-line
  }, [siloId, win.start.getTime()]);
  useEffect(() => {
    // Dashboard approval banner tap → auto-expand the frame + open the inbox
    // panel so the pending items are actually visible.
    const h = () => { setFrameOpen(true); setShowInbox(true); };
    window.addEventListener("mysilo:open-approval-inbox", h);
    return () => window.removeEventListener("mysilo:open-approval-inbox", h);
    // eslint-disable-next-line
  }, []);

  const evById = useMemo(() => Object.fromEntries((data.events || []).map((e) => [e.id, e])), [data.events]);
  const occByDate = useMemo(() => {
    // Feb 20, 2026 (pt89T — Randy) — assignee filter + hide-Google filter
    // both apply here. "all" ⇒ pass-through. Specific user id ⇒ keep
    // occurrences whose backing event either has that assignee OR has NO
    // assignee (null = "everyone" per household spec). Hide-Google ⇒ drop
    // any occurrence whose event carries a google_event_id.
    const filterAssignee = assigneeFilter && assigneeFilter !== "all" ? assigneeFilter : null;
    const hideGoogle = googleDisplayMode === "hide" || googleDisplayMode === "block";
    // Feb 22, 2026 (Sprint Request 1) — Kind Filter applied client-side.
    // "active" = show all kinds. "hide_all" = drop everything (renders as
    // an empty grid + banner). Any other value = keep only that kind.
    const kf = kindFilter || "active";
    if (kf === "hide_all") return {};
    const m = {};
    for (const o of data.occurrences || []) {
      const ev = evById[o.event_id];
      if (kf !== "active" && (ev?.kind || "event") !== kf) continue;
      if (filterAssignee) {
        const a = ev?.assignee_user_id;
        if (a && a !== filterAssignee) continue;
      }
      // pt89T (Randy) — filter field is `gcal_event_id` (backend name).
      // Was previously checking `google_event_id` which never existed on
      // the backend payload, so Hide + Block modes silently did nothing.
      if (hideGoogle && ev?.gcal_event_id) continue;
      (m[o.date] = m[o.date] || []).push(o);
    }
    return m;
  }, [data.occurrences, evById, assigneeFilter, googleDisplayMode, kindFilter]);

  const nav = (dir) => {
    if (view === "month") setCursor((c) => new Date(c.getFullYear(), c.getMonth() + dir, 1));
    else if (view === "week") setCursor((c) => addDays(c, 7 * dir));
    else setCursor((c) => addDays(c, dir));
  };
  const title = view === "month"
    ? `${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`
    : view === "week"
      ? `Week of ${MONTHS[win.start.getMonth()].slice(0, 3)} ${win.start.getDate()}`
      : view === "day"
        ? cursor.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })
        : "Next 30 days";

  const toggleAwareness = async () => {
    const next = !settings?.calendar_awareness;
    try {
      await api.post(`/calendar/${siloId}/awareness`, { enabled: next });
      setSettings((s) => ({ ...s, calendar_awareness: next }));
      toast.success(next ? "This silo's Q&A now knows your live calendar." : "Calendar hidden from the Q&A.");
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save."); }
  };

  const connectGoogle = async () => {
    try {
      const r = await api.post(`/calendar/google/auth-url`, { origin: window.location.origin, silo_id: siloId });
      // Feb 20, 2026 (Randy) — open Google consent in a NEW tab. Keeps the
      // silo page + state alive in the original tab; when the OAuth callback
      // completes it redirects to /dashboard/silo/{siloId}?gcal=connected
      // inside that new tab, which the user can close (or leave open — the
      // original tab will pick up the connected state on next /settings load).
      const win = window.open(r.data.url, "_blank", "noopener,noreferrer");
      if (!win) {
        // Popup blocked — fall back to same-tab navigation so the flow still works.
        window.location.href = r.data.url;
      } else {
        toast.message("Google consent opened in a new tab — come back here after you approve.");
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Google connect failed."); }
  };
  const syncGoogle = async () => {
    setSyncBusy(true);
    try {
      const r = await api.post(`/calendar/${siloId}/google/sync`);
      toast.success(`Synced — ${r.data.imported} events pulled from Google.`);
      load(); loadSettings();
    } catch (e) { toast.error(e.response?.data?.detail || "Sync failed."); }
    finally { setSyncBusy(false); }
  };
  const disconnectGoogle = async () => {
    if (!window.confirm("Disconnect Google Calendar? Imported events stay until the next sync removes them.")) return;
    try { await api.post(`/calendar/google/disconnect`); loadSettings(); toast.success("Google Calendar disconnected."); }
    catch { toast.error("Disconnect failed."); }
  };

  const markDone = async (occ, done) => {
    try {
      // Feb 20, 2026 (pt89T fix — Randy) — when the calendar is being
      // rendered inside KidView (kid role, kid Family View, Master
      // Dashboard kid swipe overlay), the chore-checkbox tap MUST route
      // through /propose-done so it lands in the red→green pending
      // approval queue. Previously the direct toggle bypassed parent
      // approval entirely, which regressed the reward integrity flow.
      // Owner + parent-role users still use the fast direct toggle so
      // household reality-checks are quick from the wall tablet.
      const path = kidView
        ? `/calendar/events/${occ.event_id}/propose-done`
        : `/calendar/events/${occ.event_id}/done`;
      await api.post(path, { date: occ.date, done });
      load();
    } catch { toast.error("Couldn't update."); }
  };

  return (
    <section ref={sectionRef} className={`bg-white border hairline rounded-lg ${frameOpen ? "p-5" : ""}`} data-testid="calendar-section">
      {/* Feb 20, 2026 (pt89T restructure — Randy) — header rebuilt as a
          3-column grid. Center column hosts the Master Dashboard "hero"
          button (2-row tall gradient treatment, Option B). Left column
          has the AI Calendar title + Re-theme entry (owner-only). Right
          column stacks two dropdowns: Hide Google (Active/Hide/Block) and
          Filter users (assignee filter). All old counter chips (Inbox,
          Confirmations, Tasks, +New) live in the top row of the left
          column so the row-1 vs row-2 pairing stays clean. */}
      <div className={`grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-3 items-stretch ${frameOpen ? "mb-3" : "px-5 py-3"}`} data-testid="calendar-header-grid">
        {/* ─────────────── LEFT COLUMN ─────────────── */}
        <div className="flex flex-col justify-between min-w-0">
          <div className="flex items-center gap-2 flex-wrap min-w-0">
            {/* Re-theme this Silo — owner-only, opens placeholder until
                Re-Themer Stage A/B ships. Occupies the old Master
                Dashboard chip position (top-left). */}
            {settings?.is_organizer && settings?.is_owner && !kidView && (
              <>
              <button onClick={() => setRethemeStubOpen(true)} data-testid="calendar-retheme-silo"
                title="Re-theme this Silo (AI rewrites the family/kid/parent vocabulary to fit any theme). Coming with the AI Re-Themer."
                className="text-[10px] px-2.5 py-1.5 rounded-md border border-[#C19A4A] bg-white hover:bg-[#F8F1E2] text-[#8A6A24] flex items-center gap-1 uppercase tracking-[0.14em]">
                <Palette size={12} weight="duotone" /> Re-theme
              </button>
              {/* Feb 26, 2026 (Randy pt-retheme-pencil-shortcut) — pencil
                  peer button that opens the existing ThemeWordEditorModal
                  for the active theme. Same modal reachable from the
                  Theme Archive pencil row, hoisted next to Re-theme so
                  quick word tweaks are one tap away. */}
              <button onClick={() => setThemeWordEditorOpen(true)} data-testid="calendar-retheme-edit-words"
                title="Edit the words in the active theme"
                className="p-1.5 rounded-md border border-[#C19A4A] bg-white hover:bg-[#F8F1E2] text-[#8A6A24] flex items-center">
                <PencilSimple size={12} weight="duotone" />
              </button>
              </>
            )}
            <CalendarBlank size={16} weight="duotone" className="text-[#4A5D4E]" />
            <h3 className="font-serif-display text-lg text-[#1C1B1A]" data-testid="calendar-title-heading">{tCalendarTitle}</h3>
            {loading && <Spinner size={13} className="animate-spin text-[#6B6A68]" />}
          </div>
          <div className="flex items-center gap-2 flex-wrap min-w-0 mt-2">
            {(settings?.is_owner || settings?.is_parent_role) && confirmCount > 0 && (
              <button onClick={() => setShowConfirmations(true)} data-testid="calendar-pending-confirmations"
                className="text-xs px-2.5 py-1.5 rounded-md border border-[#E86A5C] bg-white hover:bg-[#FBEBE4] flex items-center gap-1.5">
                <Check size={13} weight="bold" className="text-[#C16E5A]" /> {tPendingConfirmations}
                <span className="min-w-[16px] h-4 px-1 rounded-full bg-[#E86A5C] text-white text-[9px] flex items-center justify-center" data-testid="calendar-pending-confirmations-count">{confirmCount}</span>
              </button>
            )}
            {((data.pending || []).length > 0 || (data.pending_redemptions || []).length > 0) && (
              <button onClick={() => { setFrameOpen(true); setShowInbox((v) => !v); }} data-testid="calendar-inbox-toggle"
                className={`text-xs px-2.5 py-1.5 rounded-md border flex items-center gap-1.5 ${showInbox && frameOpen ? "bg-[#F8F1E2] border-[#C19A4A]" : "border-[#C19A4A] bg-white hover:bg-[#F8F1E2]"}`}>
                <Tray size={13} weight="duotone" className="text-[#8A6A24]" /> Inbox
                <span className="min-w-[16px] h-4 px-1 rounded-full bg-[#C19A4A] text-white text-[9px] flex items-center justify-center" data-testid="calendar-inbox-count">{(data.pending || []).length + (data.pending_redemptions || []).length}</span>
              </button>
            )}
            {frameOpen && !kidView && (
              <>
                <button onClick={() => setShowTasks((v) => !v)} data-testid="calendar-tasks-toggle"
                  className={`text-xs px-2.5 py-1.5 rounded-md border hairline flex items-center gap-1.5 ${showTasks ? "bg-[#F8F1E2] border-[#C19A4A]" : "hover:border-[#4A5D4E]"}`}>
                  <ListChecks size={13} weight="duotone" /> {tTaskPlural} ({(data.unscheduled_tasks || []).length})
                </button>
                <button onClick={() => setEditor({ defaults: { date: isoLocal(view === "agenda" ? new Date() : cursor) } })} data-testid="calendar-new-btn"
                  className="text-xs px-3 py-1.5 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] flex items-center gap-1.5">
                  <Plus size={12} weight="bold" /> New
                </button>
              </>
            )}
          </div>
        </div>

        {/* ─────────────── CENTER COLUMN — HERO ─────────────── */}
        <div className="flex items-center justify-center">
          {settings?.is_organizer && (settings?.is_owner || settings?.is_parent_role) && (
            <Link to={`/master-dashboard/${siloId}`}
              data-testid="master-dashboard-open"
              title="Open the full-screen Master Dashboard (works great on wall tablets — use Exit to come back)"
              className="group relative inline-flex flex-col items-center justify-center px-5 py-3 rounded-xl border border-[#8A6A24]/40 shadow-[0_2px_10px_rgba(138,106,36,0.25)] hover:shadow-[0_4px_18px_rgba(138,106,36,0.4)] transition-all text-center min-w-[190px]"
              style={{
                background: "linear-gradient(135deg, #FBF0D2 0%, #F1D68C 45%, #C19A4A 100%)",
                color: "#3A2A0E",
              }}>
              <Television size={18} weight="duotone" className="mb-0.5 text-[#3A2A0E]" />
              <span className="font-serif-display text-sm sm:text-base leading-tight uppercase tracking-[0.18em]" style={{ textShadow: "0 1px 0 rgba(255,255,255,0.4)" }}>
                Master
              </span>
              <span className="font-serif-display text-sm sm:text-base leading-tight uppercase tracking-[0.18em]" style={{ textShadow: "0 1px 0 rgba(255,255,255,0.4)" }}>
                Dashboard
              </span>
              <span className="pointer-events-none absolute inset-0 rounded-xl bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity" />
            </Link>
          )}
        </div>

        {/* ─────────── RIGHT COLUMN — DROPDOWNS ───────────
            pt89AA (Feb 20, 2026, Randy) — was `flex-col items-end` which
            made Google + Filter stack vertically on mobile. Now they sit
            on the SAME horizontal row (right-aligned) and wrap only when
            the viewport is narrower than the sum of both pills. */}
        <div className="flex flex-row flex-wrap justify-end items-start gap-1.5 min-w-0">
          {/* Feb 22, 2026 (Sprint Request 1) — Kind Filter (LEFT of Google
              per Randy's spec). Filters the grid to a single kind, or
              hides everything. Persists per silo in localStorage. */}
          <div className="relative">
            <button onClick={() => { setKindMenuOpen((v) => !v); setGoogleModeMenuOpen(false); setFilterMenuOpen(false); }}
              data-testid="calendar-kind-filter-btn"
              title="Show only one kind of item (or hide everything)"
              className={`text-[10px] px-2.5 py-1.5 rounded-md border hairline hover:border-[#4A5D4E] flex items-center gap-1 uppercase tracking-[0.14em] ${
                kindFilter === "hide_all" ? "border-[#C16E5A] bg-[#FBEBE4] text-[#8A2E1F]"
                : kindFilter !== "active" ? "border-[#4A5D4E] bg-[#EAF3EC] text-[#2C4A31]"
                : "bg-white text-[#6B6A68]"
              }`}>
              {kindFilter === "hide_all" ? <EyeSlash size={11} weight="duotone" /> : <Funnel size={11} weight="duotone" />}
              {kindFilter === "active" ? "Active"
                : kindFilter === "hide_all" ? "Hidden"
                : kindFilter[0].toUpperCase() + kindFilter.slice(1) + "s"}
              <CaretDown size={10} weight="bold" />
            </button>
            {kindMenuOpen && (
              <div className="absolute top-full right-0 mt-1 w-56 rounded-md border hairline bg-white shadow-lg z-[60]" data-testid="calendar-kind-filter-menu">
                {[
                  { v: "active",   label: "Active",       desc: "Show every kind (default)" },
                  { v: "event",    label: "Events only",  desc: "One-off appointments" },
                  { v: "task",     label: "Tasks only",   desc: "To-dos with a date" },
                  { v: "routine",  label: "Routines only",desc: "Recurring habit blocks" },
                  { v: "ping",     label: "Pings only",   desc: "Recurring notification questions" },
                  { v: "hide_all", label: "Hide All",     desc: "Blank slate — hide everything" },
                ].map((o) => (
                  <button key={o.v} onClick={() => setKindFilter(o.v)}
                    data-testid={`calendar-kind-filter-option-${o.v}`}
                    className={`w-full text-left px-3 py-2 hover:bg-[#F0F1EE] flex items-start gap-2 ${kindFilter === o.v ? "bg-[#F8F1E2]" : ""}`}>
                    <span className="flex-1">
                      <span className="text-xs text-[#1C1B1A] block">{o.label}{kindFilter === o.v && " ✓"}</span>
                      <span className="text-[9px] text-[#6B6A68]">{o.desc}</span>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
          {/* Hide Google dropdown. Owner-only. Active = tokens+sync+display.
              Hide = tokens+sync run silently, events NOT displayed.
              Block = sync paused, tokens preserved, events NOT displayed. */}
          {settings?.is_organizer && settings?.is_owner && !kidView && (
            <div className="relative">
              <button onClick={() => { setGoogleModeMenuOpen((v) => !v); setFilterMenuOpen(false); }}
                data-testid="calendar-hide-google-btn"
                title="Hide Google events (sync keeps running) or Block sync entirely"
                className={`text-[10px] px-2.5 py-1.5 rounded-md border hairline hover:border-[#4A5D4E] flex items-center gap-1 uppercase tracking-[0.14em] ${googleDisplayMode !== "active" ? "border-[#C16E5A] bg-[#FBEBE4] text-[#8A2E1F]" : "bg-white text-[#6B6A68]"}`}>
                {googleDisplayMode === "block" ? <Prohibit size={11} weight="duotone" />
                  : googleDisplayMode === "hide" ? <EyeSlash size={11} weight="duotone" />
                  : <GoogleLogo size={11} weight="duotone" />}
                {googleDisplayMode === "block" ? "Blocked"
                  : googleDisplayMode === "hide" ? "Hidden"
                  : "Google"}
                <CaretDown size={10} weight="bold" />
              </button>
              {googleModeMenuOpen && (
                <div className="absolute top-full right-0 mt-1 w-52 rounded-md border hairline bg-white shadow-lg z-[60]" data-testid="calendar-hide-google-menu">
                  {[
                    { v: "active", label: "Active", desc: "Tokens · sync · display all on", icon: GoogleLogo },
                    { v: "hide",   label: "Hide",   desc: "Sync still runs · events not shown", icon: EyeSlash },
                    { v: "block",  label: "Block",  desc: "Sync paused · tokens preserved", icon: Prohibit },
                  ].map((o) => (
                    <button key={o.v} onClick={() => setGoogleDisplayMode(o.v)}
                      data-testid={`calendar-hide-google-${o.v}`}
                      className={`w-full text-left px-3 py-2 hover:bg-[#F0F1EE] flex items-start gap-2 ${googleDisplayMode === o.v ? "bg-[#F8F1E2]" : ""}`}>
                      <o.icon size={13} weight="duotone" className="text-[#4A5D4E] mt-0.5 shrink-0" />
                      <span className="flex-1">
                        <span className="text-xs text-[#1C1B1A] block">{o.label}{googleDisplayMode === o.v && " ✓"}</span>
                        <span className="text-[9px] text-[#6B6A68]">{o.desc}</span>
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {/* Filter users dropdown. Filters calendar events by assignee.
              Available to owner + parent role (they see whole household).
              Hidden in kidView per Randy's spec. */}
          {(settings?.is_owner || settings?.is_parent_role) && !kidView && (settings?.household?.members || []).length > 0 && (
            <div className="relative">
              <button onClick={() => { setFilterMenuOpen((v) => !v); setGoogleModeMenuOpen(false); }}
                data-testid="calendar-filter-users-btn"
                title="Filter events by assignee"
                className={`text-[10px] px-2.5 py-1.5 rounded-md border hairline hover:border-[#4A5D4E] flex items-center gap-1 uppercase tracking-[0.14em] ${assigneeFilter !== "all" ? "border-[#4A5D4E] bg-[#EAF3EC] text-[#2C4A31]" : "bg-white text-[#6B6A68]"}`}>
                <FunnelSimple size={11} weight="duotone" />
                {assigneeFilter === "all"
                  ? "Filter"
                  : (settings?.household?.members || []).find((m) => m.user_id === assigneeFilter)?.name || "One"}
                <CaretDown size={10} weight="bold" />
              </button>
              {filterMenuOpen && (
                <div className="absolute top-full right-0 mt-1 w-56 max-h-[60vh] overflow-y-auto rounded-md border hairline bg-white shadow-lg z-[60]" data-testid="calendar-filter-users-menu">
                  <button onClick={() => { setAssigneeFilter("all"); setFilterMenuOpen(false); }}
                    data-testid="calendar-filter-users-all"
                    className={`w-full text-left px-3 py-2 hover:bg-[#F0F1EE] flex items-center gap-2 text-xs ${assigneeFilter === "all" ? "bg-[#F8F1E2]" : ""}`}>
                    <span>🌐</span> <span className="flex-1">All (Everyone){assigneeFilter === "all" && " ✓"}</span>
                  </button>
                  {(settings?.household?.members || []).map((m) => (
                    <button key={m.user_id} onClick={() => { setAssigneeFilter(m.user_id); setFilterMenuOpen(false); }}
                      data-testid={`calendar-filter-users-${m.user_id}`}
                      className={`w-full text-left px-3 py-2 hover:bg-[#F0F1EE] flex items-center gap-2 text-xs ${assigneeFilter === m.user_id ? "bg-[#F8F1E2]" : ""}`}>
                      <span>{m.emoji || (m.is_kid ? "🙂" : "👤")}</span>
                      <span className="flex-1">
                        {m.name}
                        <span className="text-[9px] text-[#6B6A68] ml-1">
                          {m.is_kid ? "(kid)" : m.is_owner ? "(owner)" : m.is_parent ? "(parent)" : ""}
                        </span>
                        {assigneeFilter === m.user_id && " ✓"}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <button onClick={() => setFrameOpen(!frameOpen)} data-testid="calendar-frame-toggle"
            title={frameOpen ? "Minimize the AI Calendar" : "Expand the AI Calendar"}
            className="p-1.5 rounded-md border hairline hover:border-[#4A5D4E] text-[#6B6A68] self-end">
            {frameOpen ? <CaretUp size={13} weight="bold" /> : <CaretDown size={13} weight="bold" />}
          </button>
        </div>
      </div>

      {/* pt89T (Feb 20, 2026, Randy) — Re-Themer Stage B modal wired to
          the "Re-theme" button in the header. The old placeholder stub
          has been retired now that Stage B ships live LLM-driven
          rewriting + apply. */}
      {rethemeStubOpen && (
        <CalendarThemeModal
          siloId={siloId}
          open={rethemeStubOpen}
          onClose={() => setRethemeStubOpen(false)}
        />
      )}
      {themeWordEditorOpen && (
        <ThemeWordEditorModal
          siloId={siloId}
          onClose={() => setThemeWordEditorOpen(false)}
        />
      )}

      {frameOpen && (
        <>
          {/* nav + view switcher */}
          <div className="flex items-center gap-2 flex-wrap mb-3">
            <button onClick={() => nav(-1)} data-testid="calendar-prev" className="p-1.5 rounded border hairline hover:border-[#4A5D4E]"><CaretLeft size={13} /></button>
            <button onClick={() => setCursor(new Date())} data-testid="calendar-today" className="text-xs px-2.5 py-1.5 rounded border hairline hover:border-[#4A5D4E]">{tToday}</button>
            <button onClick={() => nav(1)} data-testid="calendar-next" className="p-1.5 rounded border hairline hover:border-[#4A5D4E]"><CaretRight size={13} /></button>
            <span className="text-sm font-medium text-[#1C1B1A] ml-1" data-testid="calendar-title">{title}</span>
            {loading && <Spinner size={13} className="animate-spin text-[#6B6A68]" />}
            <span className="flex-1" />
            <div className="flex rounded-md border hairline overflow-hidden">
              {["month", "week", "day", "agenda"].map((v) => (
                <button key={v} onClick={() => setView(v)} data-testid={`calendar-view-${v}`}
                  className={`text-[11px] px-2.5 py-1.5 capitalize ${view === v ? "bg-[#1C1B1A] text-[#FDFCF8]" : "text-[#6B6A68] hover:bg-[#F0F1EE]"}`}>
                  {v}
                </button>
              ))}
            </div>
          </div>

          {/* tasks drawer */}
          {showTasks && <TasksDrawer siloId={siloId} tasks={data.unscheduled_tasks || []} onChanged={load} onSchedule={(t) => setEditor({ event: t })} />}

          {/* approval inbox (Randy 2A — kid-safe adds land here first) */}
          {showInbox && ((data.pending || []).length > 0 || (data.pending_redemptions || []).length > 0) && (
            <ApprovalInbox siloId={siloId} pending={data.pending} pendingRedemptions={data.pending_redemptions || []} isOwner={!!data.is_owner} onChanged={load} />
          )}

          {/* the grid / lists */}
          {kindFilter === "hide_all" && (
            <div className="text-xs text-[#8A2E1F] italic bg-[#FBEBE4] border border-[#C16E5A] rounded-md px-3 py-2 mb-2"
              data-testid="calendar-kind-filter-hidden-banner">
              Every item is hidden. Change the filter to <strong>Active</strong> (top-right) to see events again.
            </div>
          )}
          {view === "month" && (
            <MonthGrid win={win} cursor={cursor} occByDate={occByDate} evById={evById}
              onDay={(d) => {
                setCursor(d);
                setView("day");
                // Feb 20, 2026 (pt89T fix — Randy) — day click was leaving
                // the browser scrolled to the bottom of the calendar frame
                // (day-view is much taller than the collapsed month cell
                // so the layout jump misaligned the focused button).
                // Explicitly re-center the newly-mounted DayList after
                // React commits the switch. rAF guarantees the element
                // exists before we look it up. block:"center" keeps the
                // clicked day comfortably in the viewport instead of
                // slamming to bottom.
                requestAnimationFrame(() => {
                  const el = document.querySelector(`[data-testid="calendar-daylist-${isoLocal(d)}"]`);
                  if (el && typeof el.scrollIntoView === "function") {
                    el.scrollIntoView({ behavior: "smooth", block: "center" });
                  }
                });
              }} />
          )}
          {view === "week" && (
            <div className="space-y-2" data-testid="calendar-week">
              {Array.from({ length: 7 }, (_, i) => addDays(win.start, i)).map((d) => (
                <DayList key={isoLocal(d)} date={d} occs={occByDate[isoLocal(d)] || []} evById={evById}
                  streaks={data.streaks} onEdit={(ev) => (kidView ? setPeeker({ event: ev }) : setEditor({ event: ev }))} onDone={markDone} compact />
              ))}
            </div>
          )}
          {view === "day" && (
            <DayList date={cursor} occs={occByDate[isoLocal(cursor)] || []} evById={evById}
              streaks={data.streaks} onEdit={(ev) => (kidView ? setPeeker({ event: ev }) : setEditor({ event: ev }))} onDone={markDone} />
          )}
          {view === "agenda" && (
            <div className="space-y-2" data-testid="calendar-agenda">
              {Object.keys(occByDate).sort().map((ds) => (
                <DayList key={ds} date={parseLocal(ds)} occs={occByDate[ds]} evById={evById}
                  streaks={data.streaks} onEdit={(ev) => (kidView ? setPeeker({ event: ev }) : setEditor({ event: ev }))} onDone={markDone} compact />
              ))}
              {Object.keys(occByDate).length === 0 && <div className="text-xs text-[#6B6A68] italic py-4">Nothing scheduled in the next 30 days.</div>}
            </div>
          )}

          {/* awareness + google row — HIDDEN for kid/non-parent role.
              Randy Feb 20, 2026 (kid-view read-only) — kids and non-parent
              collaborators must not be able to toggle calendar awareness
              or connect a Google Calendar to the household silo. */}
          {!kidView && (
          <div className="mt-4 pt-3 border-t hairline flex flex-col sm:flex-row gap-3 sm:items-center">
            <label className="flex items-start gap-2 cursor-pointer flex-1" data-testid="calendar-awareness-toggle">
              <input type="checkbox" checked={!!settings?.calendar_awareness} onChange={toggleAwareness} className="mt-0.5" />
              <span className="text-[11px] text-[#1C1B1A] leading-relaxed">
                <strong>This silo knows my calendar.</strong>
                <span className="block text-[#6B6A68] mt-0.5">
                  Injects a live, always-current digest of this calendar into the silo&apos;s Q&amp;A — so it can answer
                  &ldquo;what&apos;s on my agenda&rdquo; and find open slots. {settings?.silo_kind === "personal" ? "On by default for personal silos." : "Off by default for public silos."}
                </span>
              </span>
            </label>
            <div className="flex items-center gap-2 flex-wrap" data-testid="calendar-google-card">
              {!settings?.google?.configured ? (
                <button onClick={() => setGuideOpen(true)} data-testid="google-setup-howto-btn"
                  className="text-xs px-3 py-2 rounded-md border hairline hover:border-[#4A5D4E] flex items-center gap-1.5">
                  <GoogleLogo size={13} weight="bold" /> How to set up Google Calendar sync
                </button>
              ) : !settings?.google?.connected ? (
                <>
                  <button onClick={connectGoogle} data-testid="google-connect-btn"
                    className="text-xs px-3 py-2 rounded-md border hairline hover:border-[#4A5D4E] flex items-center gap-1.5">
                    <GoogleLogo size={13} weight="bold" /> Connect Google Calendar
                  </button>
                  <button onClick={() => setGuideOpen(true)} data-testid="google-setup-howto-btn"
                    className="text-[10px] underline text-[#6B6A68] hover:text-[#4A5D4E]">how to</button>
                </>
              ) : (
                <>
                  <span className="text-[10px] text-[#2F6B3A] flex items-center gap-1"><Check size={11} weight="bold" /> {settings.google.email || "Google connected"}</span>
                  <button onClick={syncGoogle} disabled={syncBusy} data-testid="google-sync-btn"
                    className="text-xs px-2.5 py-1.5 rounded-md border hairline hover:border-[#4A5D4E] flex items-center gap-1 disabled:opacity-50">
                    <ArrowsClockwise size={12} className={syncBusy ? "animate-spin" : ""} /> Sync now
                  </button>
                  <button onClick={() => setGuideOpen(true)} data-testid="google-setup-howto-btn"
                    className="text-[10px] underline text-[#6B6A68] hover:text-[#4A5D4E]">how to</button>
                  <button onClick={disconnectGoogle} data-testid="google-disconnect-btn" className="text-[10px] underline text-[#6B6A68] hover:text-[#C16E5A]">disconnect</button>
                </>
              )}
            </div>
          </div>
          )}

          {/* ─────────── The four collapsible sub-buttons (Feb 2026) ───────────
              Order + naming per user's spec:
                1) Calendar Pings   (was "Organizer pings")
                2) Household Setup  (was "Household & quiet hours")
                3) Reward store
                4) Family leaderboard
              Each defaults minimized; state persisted per silo.
              Feb 2026 (Randy) — kidView hides the whole sub-button stack.
              Used for the calendar-only surfaces (Master Dashboard kid
              swipe overlay, kid-role Family View). */}
          {!kidView && (
          <div className="mt-3 pt-3 border-t hairline space-y-2" data-testid="calendar-subbuttons">
            <SubButton
              open={pingsOpen} setOpen={setPingsOpen}
              icon={<BellRinging size={13} weight="duotone" className="text-[#C16E5A]" />}
              label={tPingPlural} testId="calendar-btn-pings">
              <OrganizerPingsSection siloId={siloId} inheritedSkus={inheritedSkus} embedded showInfo
                members={settings?.household?.members || []} />
            </SubButton>

            {settings?.is_organizer && (
              <SubButton
                open={householdOpen} setOpen={setHouseholdOpen}
                icon={<UsersThree size={13} weight="duotone" className="text-[#4A5D4E]" />}
                label={tHouseholdTitle} testId="calendar-btn-household">
                <HouseholdPanel siloId={siloId} siloName={siloName} settings={settings} onChanged={loadSettings} />
              </SubButton>
            )}

            {/* R5 (pt89X) — Personal Theme Archive. Positioned between
                Household Setup and Reward store per Randy's R5 spec. Owner
                only — kid/parent-role collabs don't need to see it. */}
            {settings?.is_organizer && settings?.is_owner && (
              <SubButton
                open={themeArchiveOpen} setOpen={setThemeArchiveOpen}
                icon={<Palette size={13} weight="duotone" className="text-[#8A6AC1]" />}
                label={tThemeArchiveLabel} testId="calendar-btn-theme-archive">
                <ThemeArchivePanel siloId={siloId} />
              </SubButton>
            )}

            {settings?.is_organizer && (settings?.is_owner || settings?.is_parent_role) && (
              <SubButton
                open={rewardsOpen} setOpen={setRewardsOpen}
                icon={<Gift size={13} weight="duotone" className="text-[#C16E5A]" />}
                label={tRewardStoreLabel} testId="calendar-btn-rewards">
                <FamilyRewards siloId={siloId} panel="store" />
              </SubButton>
            )}

            {settings?.is_organizer && (settings?.is_owner || settings?.is_parent_role) && (
              <SubButton
                open={leaderboardOpen} setOpen={setLeaderboardOpen}
                icon={<Trophy size={13} weight="duotone" className="text-[#C19A4A]" />}
                label={tLeaderboardLabel} testId="calendar-btn-leaderboard">
                <FamilyRewards siloId={siloId} panel="leaderboard" />
              </SubButton>
            )}

            {/* Feb 2026 (Randy) — Family Digest: weekly rollup. Visible to
                owner AND to promoted parents (backend gates on parent_ids).
                In the AI Calendar owner view we simply show it for owners;
                promoted parents access it from the Family View surface. */}
            {settings?.is_organizer && (settings?.is_owner || settings?.is_parent_role) && (
              <SubButton
                open={digestOpen} setOpen={setDigestOpen}
                icon={<CalendarBlank size={13} weight="duotone" className="text-[#4A5D4E]" />}
                label={tDigestLabel} testId="calendar-btn-digest">
                <FamilyDigest siloId={siloId} />
              </SubButton>
            )}
          </div>
          )}
        </>
      )}

      <GoogleCalendarSetupGuide open={guideOpen} onClose={() => setGuideOpen(false)} onSaved={() => loadSettings()} />

      {editor && (
        <EventEditorModal siloId={siloId} editing={editor.event || null} defaults={editor.defaults || {}}
          members={settings?.household?.members || []}
          onClose={() => setEditor(null)}
          onSaved={() => { setEditor(null); load(); }} />
      )}
      {peeker && (
        <EventPeekModal event={peeker.event} onClose={() => setPeeker(null)} />
      )}
      {showConfirmations && (
        <PendingConfirmationsModal siloId={siloId} siloName={siloName}
          onClose={() => setShowConfirmations(false)}
          onCountChanged={(n) => setConfirmCount(n)} />
      )}
    </section>
  );
}

// Feb 2026 — a single collapsible sub-button used to stack Calendar Pings,
// Household Setup, Reward store and Family leaderboard inside the AI
// Calendar frame. Same look/feel as the older "Household & quiet hours"
// button; the caret reveals the label state at a glance.
function SubButton({ open, setOpen, icon, label, testId, children }) {
  return (
    <div data-testid={`${testId}-wrap`}>
      <button onClick={() => setOpen(!open)} data-testid={testId}
        className={`w-full text-left text-xs px-2.5 py-1.5 rounded-md border hairline hover:border-[#4A5D4E] flex items-center gap-1.5 ${open ? "bg-[#F0F1EE]" : "bg-white"}`}>
        {icon}
        <span className="flex-1 text-[#1C1B1A]">{label}</span>
        {open ? <CaretUp size={11} weight="bold" className="text-[#6B6A68]" /> : <CaretDown size={11} weight="bold" className="text-[#6B6A68]" />}
      </button>
      {open && (
        <div className="mt-2" data-testid={`${testId}-body`}>{children}</div>
      )}
    </div>
  );
}

function ApprovalInbox({ siloId, pending, pendingRedemptions = [], isOwner, onChanged }) {
  const [busyId, setBusyId] = useState(null);
  const act = async (ev, action) => {
    setBusyId(ev.id);
    try {
      await api.post(`/calendar/events/${ev.id}/${action}`);
      toast.success(action === "approve" ? `"${ev.title}" is on the family calendar.` : `"${ev.title}" declined.`);
      window.dispatchEvent(new Event("mysilo:calendar-refresh"));
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't do that."); }
    finally { setBusyId(null); }
  };
  const redeemAct = async (red, action) => {
    setBusyId(red.id);
    try {
      await api.post(`/calendar/${siloId}/redemptions/${red.id}/${action}`);
      toast.success(action === "approve" ? `Approved: ${red.reward_name}.` : `Declined: ${red.reward_name}.`);
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't do that."); }
    finally { setBusyId(null); }
  };
  return (
    <div className="border border-[#C19A4A] rounded-md p-3 mb-3 bg-[#FDF9EF]" data-testid="calendar-approval-inbox">
      <div className="text-[10px] uppercase tracking-[0.18em] text-[#8A6A24] mb-2 flex items-center gap-1.5">
        <Tray size={12} weight="duotone" /> Approval inbox
        <span className="normal-case tracking-normal text-[#6B6A68]">
          — {isOwner ? "family members proposed these; nothing lands until you say so" : "waiting for the calendar owner"}
        </span>
      </div>
      <div className="space-y-1.5">
        {/* reward redemption requests (Randy — same inbox) */}
        {pendingRedemptions.map((red) => (
          <div key={red.id} className="flex items-center gap-2 text-sm bg-white border border-[#EAD9AE] rounded px-2.5 py-1.5" data-testid={`redemption-pending-${red.id}`}>
            <span className="text-lg leading-none shrink-0">{red.reward_emoji}</span>
            <div className="flex-1 min-w-0">
              <div className="truncate text-[#1C1B1A]">Reward · {red.reward_name}</div>
              <div className="text-[10px] text-[#6B6A68]">{red.cost_points} pts · from {red.redeemer_name}</div>
            </div>
            {isOwner ? (
              <>
                <button onClick={() => redeemAct(red, "approve")} disabled={busyId === red.id} data-testid={`redemption-approve-${red.id}`}
                  className="text-[10px] px-2.5 py-1.5 rounded bg-[#4A5D4E] text-white hover:bg-[#3A4A3E] disabled:opacity-50 flex items-center gap-1">
                  <Check size={11} weight="bold" /> Approve
                </button>
                <button onClick={() => redeemAct(red, "decline")} disabled={busyId === red.id} data-testid={`redemption-decline-${red.id}`}
                  className="text-[10px] px-2.5 py-1.5 rounded border hairline text-[#C16E5A] hover:border-[#C16E5A] disabled:opacity-50">
                  Decline
                </button>
              </>
            ) : (
              <span className="text-[10px] text-[#8A6A24] italic shrink-0">waiting</span>
            )}
          </div>
        ))}
        {(pending || []).map((ev) => {
          const meta = KIND_META[ev.kind] || KIND_META.event;
          return (
            <div key={ev.id} className="flex items-center gap-2 text-sm bg-white border hairline rounded px-2.5 py-1.5" data-testid={`calendar-pending-${ev.id}`}>
              <span className="inline-block w-1.5 h-1.5 rounded-full shrink-0" style={{ background: meta.dot }} />
              <div className="flex-1 min-w-0">
                <div className="truncate text-[#1C1B1A]">{ev.title || ev.ping_question}</div>
                <div className="text-[10px] text-[#6B6A68]">
                  {meta.label}{ev.start_date ? ` · ${ev.start_date}` : ""}{ev.start_time ? ` · ${fmt12(ev.start_time)}` : ""}
                  {ev.proposed_by_name ? ` · from ${ev.proposed_by_name}` : ""}
                </div>
              </div>
              {isOwner ? (
                <>
                  <button onClick={() => act(ev, "approve")} disabled={busyId === ev.id} data-testid={`calendar-approve-${ev.id}`}
                    className="text-[10px] px-2.5 py-1.5 rounded bg-[#4A5D4E] text-white hover:bg-[#3A4A3E] disabled:opacity-50 flex items-center gap-1">
                    <Check size={11} weight="bold" /> Approve
                  </button>
                  <button onClick={() => act(ev, "decline")} disabled={busyId === ev.id} data-testid={`calendar-decline-${ev.id}`}
                    className="text-[10px] px-2.5 py-1.5 rounded border hairline text-[#C16E5A] hover:border-[#C16E5A] disabled:opacity-50">
                    Decline
                  </button>
                </>
              ) : (
                <span className="text-[10px] text-[#8A6A24] italic shrink-0">waiting</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


function MonthGrid({ win, cursor, occByDate, evById, onDay }) {
  const todayIso = isoLocal(new Date());
  const cells = Array.from({ length: 42 }, (_, i) => addDays(win.start, i));
  return (
    <div data-testid="calendar-month">
      <div className="grid grid-cols-7 text-center text-[9px] uppercase tracking-[0.18em] text-[#6B6A68] mb-1">
        {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => <div key={d} className="py-1">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 border-l border-t hairline">
        {cells.map((d) => {
          const ds = isoLocal(d);
          const occs = occByDate[ds] || [];
          const inMonth = d.getMonth() === cursor.getMonth();
          return (
            <button key={ds} onClick={() => onDay(d)} data-testid={`calendar-day-${ds}`}
              className={`min-h-[64px] sm:min-h-[84px] border-r border-b hairline p-1 text-left align-top hover:bg-[#F0F1EE]/60 transition-colors ${inMonth ? "bg-white" : "bg-[#FBFAF6]"}`}>
              {/* Feb 24, 2026 (Randy pt89c-xxi) — today marker recolored
                  from near-black to the sprint's green so it POPS as
                  "you are here today" in the month grid. */}
              <div className={`text-[10px] w-5 h-5 flex items-center justify-center rounded-full ${ds === todayIso ? "bg-[#4A9E6A] text-white" : inMonth ? "text-[#1C1B1A]" : "text-[#B5B3AE]"}`}>
                {d.getDate()}
              </div>
              <div className="space-y-0.5 mt-0.5">
                {occs.slice(0, 3).map((o, i) => {
                  const ev = evById[o.event_id] || {};
                  const meta = KIND_META[o.kind] || KIND_META.event;
                  return (
                    <div key={i} className={`text-[9px] leading-tight truncate rounded px-1 py-0.5 ${o.done ? "line-through opacity-50" : ""}`}
                      style={{ background: meta.bg, color: "#1C1B1A" }}>
                      <span className="inline-block w-1.5 h-1.5 rounded-full mr-1 align-middle" style={{ background: meta.dot }} />
                      {ev.title || ev.ping_question || "•"}
                    </div>
                  );
                })}
                {occs.length > 3 && <div className="text-[9px] text-[#6B6A68]">+{occs.length - 3} more</div>}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// EventPeekModal — Feb 20, 2026 (Randy Photo 1) — read-only event drawer
// shown to kids and non-parent collaborators when they tap an event on
// the calendar. Displays the title, date, kind and description; no
// edit/delete controls. Kids still complete the chore via the checkbox
// on the DayList row (goes through the existing propose-done flow).
function EventPeekModal({ event, onClose }) {
  const ev = event || {};
  const meta = KIND_META[ev.kind] || KIND_META.event;
  const dateLabel = ev.date ? new Date(ev.date + "T00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" }) : "";
  const timeLabel = ev.all_day || !ev.time ? "All day" : fmt12(ev.time);
  return (
    <div className="fixed inset-0 z-[300] bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="event-peek-modal">
      <div onClick={(e) => e.stopPropagation()} className="modal-safe w-full sm:max-w-md bg-[#FDFCF8] rounded-t-2xl sm:rounded-2xl border hairline shadow-xl overflow-hidden">
        <div className="px-5 py-4 flex items-center justify-between border-b hairline">
          <div className="flex items-center gap-2 min-w-0">
            <span className="inline-block w-2 h-2 rounded-full" style={{ background: meta.dot }} />
            <span className="text-[10px] uppercase tracking-[0.2em] text-[#6B6A68]">{meta.label}</span>
          </div>
          <button onClick={onClose} data-testid="event-peek-close" className="p-1 rounded-md hover:bg-black/5" aria-label="Close">
            <X size={16} weight="bold" className="text-[#1C1B1A]" />
          </button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <h3 className="font-serif-display text-xl text-[#1C1B1A]" data-testid="event-peek-title">
            {ev.title || ev.ping_question || "Untitled"}
          </h3>
          {dateLabel && (
            <div className="text-sm text-[#1C1B1A]" data-testid="event-peek-when">
              {dateLabel}{ev.time ? ` · ${timeLabel}` : ` · ${timeLabel}`}
            </div>
          )}
          {ev.description && (
            <div className="text-sm text-[#2C2B2A] whitespace-pre-wrap leading-relaxed" data-testid="event-peek-description">
              {ev.description}
            </div>
          )}
          {ev.assignee_name && (
            <div className="text-xs text-[#2C2B2A]" data-testid="event-peek-assignee">
              For: <strong>{ev.assignee_name}</strong>
            </div>
          )}
          {ev.kind === "ping" && ev.ping_question && ev.ping_question !== ev.title && (
            <div className="text-sm text-[#2C2B2A] italic" data-testid="event-peek-ping">
              &ldquo;{ev.ping_question}&rdquo;
            </div>
          )}
          <div className="pt-2 text-[11px] text-[#6B6A68]">
            Tap the checkbox on the day list to mark this done — a parent will confirm it.
          </div>
        </div>
        <div className="px-5 py-3 border-t hairline bg-white flex justify-end">
          <button onClick={onClose} data-testid="event-peek-done" className="text-sm px-4 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E]">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function DayList({ date, occs, evById, streaks, onEdit, onDone, compact = false }) {
  const ds = isoLocal(date);
  const isToday = ds === isoLocal(new Date());
  return (
    <div className={compact ? "" : "border hairline rounded-md p-3"} data-testid={`calendar-daylist-${ds}`}>
      <div className={`text-[11px] font-medium ${isToday ? "text-[#4A5D4E]" : "text-[#6B6A68]"} mb-1.5`}>
        {date.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}{isToday ? " · today" : ""}
      </div>
      {occs.length === 0 ? (
        <div className="text-[11px] text-[#B5B3AE] italic">{compact ? "—" : "Nothing scheduled."}</div>
      ) : (
        <div className="space-y-1">
          {occs.map((o, i) => {
            const ev = evById[o.event_id] || {};
            const meta = KIND_META[o.kind] || KIND_META.event;
            // Feb 22, 2026 (Randy pt89c-xi) — events NOW get checkboxes too.
            // A chore that landed as `kind: "event"` (e.g., "Clean Downstairs
            // Bathroom on Friday, July 24") is still a chore — it should be
            // check-off-able. The backend /events/{id}/done endpoint already
            // supports non-routine, non-task kinds; only the frontend gate
            // was excluding them. Pings stay excluded because a "done" ping
            // doesn't make sense.
            const canDone = o.kind === "routine" || o.kind === "task" || o.kind === "event";
            const isPending = !!o.pending && !o.done;
            const isDone = !!o.done;
            const assigneeId = ev.assignee_user_id;
            const personColor = assigneeId ? assigneeHslColor(assigneeId) : null;
            const firstName = (ev.assignee_name || "").trim().split(/\s+/)[0] || "";
            // Three-state checkbox class:
            //   empty (grey border) → pending (red border + red check) → done (solid green + white check)
            const boxClass = isDone
              ? "bg-[#4A5D4E] border-[#4A5D4E] text-white"
              : isPending
              ? "border-[#C16E5A] text-[#C16E5A] bg-[#FBEBE4]"
              : "border-[#B5B3AE]";
            return (
              <div key={i}
                className="flex items-center gap-2 text-sm bg-[#FDFCF8] border hairline rounded px-2.5 py-1.5"
                style={personColor ? { borderLeft: `3px solid ${personColor}` } : undefined}
                data-testid={`calendar-occ-${o.event_id}-${o.date}`}>
                {canDone && (
                  <button onClick={() => onDone(o, !o.done)} data-testid={`calendar-done-${o.event_id}-${o.date}`}
                    title={isPending ? "Waiting for parent approval" : isDone ? "Done" : "Mark done"}
                    className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${boxClass}`}>
                    {(isDone || isPending) && <Check size={10} weight="bold" />}
                  </button>
                )}
                <span
                  className="inline-block w-1.5 h-1.5 rounded-full shrink-0"
                  style={{ background: personColor || meta.dot }}
                  title={firstName ? `Assigned to ${firstName}` : meta.label} />
                <button onClick={() => onEdit(ev)} className={`flex-1 text-left min-w-0 truncate ${isDone ? "line-through opacity-50" : ""}`}>
                  <span className="text-[#1C1B1A]">{ev.title || ev.ping_question}</span>
                  {ev.kind === "ping" && ev.ping_question && ev.title !== ev.ping_question && (
                    <span className="text-[#6B6A68]"> · {ev.ping_question.slice(0, 60)}</span>
                  )}
                </button>
                {firstName && (
                  <span
                    className="text-[10px] font-medium px-1.5 py-0.5 rounded shrink-0"
                    style={{ background: `${personColor}22`, color: personColor }}
                    data-testid={`calendar-occ-assignee-${o.event_id}-${o.date}`}
                    title={ev.assignee_name}>
                    → {firstName}
                  </span>
                )}
                {o.kind === "routine" && (streaks[o.event_id] || 0) > 1 && (
                  <span className="text-[9px] text-[#2C6E92] bg-[#E3EFF7] rounded px-1 shrink-0">×{streaks[o.event_id]} streak</span>
                )}
                {(ev.reminders || []).length > 0 && <Bell size={11} className="text-[#6B6A68] shrink-0" />}
                {ev.source === "gcal" && <GoogleLogo size={11} className="text-[#6B6A68] shrink-0" title="From Google Calendar" />}
                <span className="text-[10px] text-[#6B6A68] shrink-0">{o.all_day ? "all day" : fmt12(o.start_time)}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Feb 22, 2026 (Randy pt89c-vii) — stable per-person color mapping. Any
// assignee_user_id (real user id OR kid:{profile_id}) hashes to a bright,
// saturated HSL hue that's consistent across the whole day list. Golden-
// angle-style spacing keeps sibling colors distinct even with 4-8 members.
function assigneeHslColor(id) {
  let h = 0;
  const s = String(id || "");
  for (let i = 0; i < s.length; i += 1) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  const hue = Math.abs(h * 137) % 360; // 137 ≈ golden angle in degrees
  return `hsl(${hue}, 62%, 42%)`;
}

function TasksDrawer({ siloId, tasks, onChanged, onSchedule }) {
  const [text, setText] = useState("");
  const add = async () => {
    const t = text.trim();
    if (!t) return;
    try {
      await api.post(`/calendar/${siloId}/events`, { kind: "task", title: t, tz_offset_minutes: new Date().getTimezoneOffset() });
      setText(""); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't add the task."); }
  };
  const toggle = async (t) => {
    try { await api.post(`/calendar/events/${t.id}/done`, { done: !t.done }); onChanged(); } catch { /* */ }
  };
  const del = async (t) => {
    try { await api.delete(`/calendar/events/${t.id}`); onChanged(); } catch { /* */ }
  };
  return (
    <div className="border hairline rounded-md p-3 mb-3 bg-[#FBFAF6]" data-testid="calendar-tasks-drawer">
      <div className="flex gap-2 mb-2">
        <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="Quick task (no date — schedule it later)" data-testid="calendar-task-input"
          className="flex-1 px-2.5 py-1.5 border hairline rounded text-sm bg-white focus:outline-none focus:border-[#4A5D4E]" />
        <button onClick={add} disabled={!text.trim()} data-testid="calendar-task-add"
          className="text-xs px-3 rounded bg-[#1C1B1A] text-white disabled:opacity-50">Add</button>
      </div>
      {tasks.length === 0 ? <div className="text-[11px] text-[#6B6A68] italic">No open tasks.</div> : (
        <div className="space-y-1">
          {tasks.map((t) => (
            <div key={t.id} className="flex items-center gap-2 text-sm bg-white border hairline rounded px-2.5 py-1.5" data-testid={`calendar-task-${t.id}`}>
              <button onClick={() => toggle(t)} className={`w-4 h-4 rounded border flex items-center justify-center ${t.done ? "bg-[#4A5D4E] border-[#4A5D4E] text-white" : "border-[#B5B3AE]"}`}>
                {t.done && <Check size={10} weight="bold" />}
              </button>
              <span className={`flex-1 ${t.done ? "line-through opacity-50" : ""}`}>{t.title}</span>
              <button onClick={() => onSchedule(t)} className="text-[10px] underline text-[#6B6A68] hover:text-[#4A5D4E]">schedule</button>
              <button onClick={() => del(t)} className="text-[#6B6A68] hover:text-[#C16E5A]"><Trash size={13} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

